import { Injectable } from '@angular/core';
import {
  COLUMN_DATA_TYPE,
  formatBoolean,
  formatDataTypeAction,
  formatDataTypeActionIconList,
  formatDataTypeCurrency,
  formatDataTypeList,
  formatDataTypeNumber,
  formatDataTypeParams,
  formatDataTypePercent,
} from '@emma-components/emma-table/emma-table.helpers';
import { formatDataTypeTimeWindow } from '@emma-components/emma-table/emma-table.i18n';
import { cleanHtmlTags } from '@emma-helpers/emma-utils';
import { ExportToCsv, Options } from 'export-to-csv';
import { get, isArray } from 'lodash';
import {
  EMMATableColumn,
  CellParam,
  CellCommonTypes,
  CellAllTypes,
} from '@emma-components/emma-table/emma-table.types';
import { TIME_UNIT } from 'emma-common-ts';

export interface ExportCsvOptions {
  rows: unknown[];
  columns: EMMATableColumn[];
  filename: string;
}

const formatEnsureString = (data: CellAllTypes): string => {
  if (data === null || data === undefined) {
    return '';
  }
  return String(data);
};

const formatCellCases = (data: CellAllTypes, col: EMMATableColumn): string => {
  switch (col.dataType) {
    case COLUMN_DATA_TYPE.TIME_WINDOW:
      data = formatDataTypeTimeWindow(data as CellCommonTypes, col.unit as TIME_UNIT);
      break;
    case COLUMN_DATA_TYPE.CURRENCY:
      data = formatDataTypeCurrency(data as CellCommonTypes, col.unit ?? col.units);
      break;
    case COLUMN_DATA_TYPE.PARAMS:
      data = formatDataTypeParams(data as CellParam[] | null);
      break;
    case COLUMN_DATA_TYPE.LIST:
      data = formatDataTypeList(data as string[] | number[] | undefined);
      break;
    case COLUMN_DATA_TYPE.NUMBER:
      data = formatDataTypeNumber(data as CellCommonTypes);
      break;
    case COLUMN_DATA_TYPE.HTML:
      data = cleanHtmlTags(data as string).trim();
      break;
    case COLUMN_DATA_TYPE.LIST_LENGTH:
      data = formatDataTypeNumber(isArray(data) ? data.length : 0);
      break;
    case COLUMN_DATA_TYPE.ACTION_ICON_LIST:
      data = formatDataTypeActionIconList(data as { label: string });
      break;
    case COLUMN_DATA_TYPE.ACTION:
      data = formatDataTypeAction(data as CellCommonTypes | { label: string });
      break;
    case COLUMN_DATA_TYPE.BOOLEAN:
      data = formatBoolean(data as boolean);
      break;
    case COLUMN_DATA_TYPE.PERCENT:
      data = formatDataTypePercent(data as CellCommonTypes);
      break;
  }
  return formatEnsureString(data);
};

@Injectable({
  providedIn: 'root',
})
export class CsvExporterService {
  processCellData = (data: CellAllTypes, col: EMMATableColumn): string => {
    return formatCellCases(data, col);
  };

  exportCsv(options: ExportCsvOptions) {
    const optionsLib: Options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      useBom: true,
      useKeysAsHeaders: false,
      filename: options.filename,
      headers: options.columns.map((c) => c.name || '').filter((c) => c),
    };
    const data = options.rows.map((row) =>
      options.columns.reduce((acc: { [key: string]: string }, col) => {
        acc[col.name] = this.processCellData(get(row, col.prop), col);
        return acc;
      }, {})
    );
    const csvExporter = new ExportToCsv(optionsLib);
    csvExporter.generateCsv(data);
    return data;
  }
}
